/**
 * Signup legal gates. Creating an account requires explicit TOS/Privacy
 * acceptance and 18+ confirmation. Community Guidelines are agreed on the
 * onboarding step right after email verification.
 */

export const TOS_ACCEPTANCE_KEY = 'tos-and-privacy';
export const GUIDELINES_ACCEPTED_KEY = 'communityGuidelinesAccepted';
export const GUIDELINES_ACCEPTED_AT_KEY = 'communityGuidelinesAcceptedAt';

export const hasAcceptedTerms = terms =>
  Array.isArray(terms) && terms.includes(TOS_ACCEPTANCE_KEY);

export const hasConfirmedAge18 = confirmAge18 => confirmAge18 === true;

export const hasAcceptedGuidelines = agreeGuidelines => agreeGuidelines === true;

// Community Guidelines are agreed right after email verification (Sam's
// order: sign up → verify email → Guidelines → ID check), not on the form.
export const canCreateAccount = values =>
  hasAcceptedTerms(values?.terms) && hasConfirmedAge18(values?.confirmAge18);

/**
 * Protected-data stamp written at signup and on the one-time existing-user prompt.
 *
 * @param {Date} [now]
 * @returns {{ communityGuidelinesAccepted: true, communityGuidelinesAcceptedAt: string }}
 */
export const pickGuidelinesProtectedData = (now = new Date()) => ({
  [GUIDELINES_ACCEPTED_KEY]: true,
  [GUIDELINES_ACCEPTED_AT_KEY]: now.toISOString(),
});

/**
 * True when this user already stamped Community Guidelines acceptance.
 *
 * @param {Object} [user]
 * @returns {boolean}
 */
export const userHasAcceptedCommunityGuidelines = user => {
  const data = user?.attributes?.profile?.protectedData || {};
  return (
    data[GUIDELINES_ACCEPTED_KEY] === true && typeof data[GUIDELINES_ACCEPTED_AT_KEY] === 'string'
  );
};

/**
 * Merge guidelines stamp onto currentUser for an optimistic UI update.
 *
 * @param {Object} user
 * @param {Object} protectedData
 * @returns {Object}
 */
export const applyGuidelinesToUser = (user, protectedData) => {
  if (!user?.attributes?.profile) {
    return user;
  }
  return {
    ...user,
    attributes: {
      ...user.attributes,
      profile: {
        ...user.attributes.profile,
        protectedData: {
          ...(user.attributes.profile.protectedData || {}),
          ...protectedData,
        },
      },
    },
  };
};

/**
 * Existing-user prompt: dashboard / next sign-in, never inside a live call.
 *
 * @param {{ currentUser?: Object, pathname?: string }} [input]
 * @returns {boolean}
 */
export const shouldShowGuidelinesConsentGate = ({ currentUser, pathname } = {}) => {
  if (!currentUser?.id) {
    return false;
  }
  if (userHasAcceptedCommunityGuidelines(currentUser)) {
    return false;
  }
  const path = typeof pathname === 'string' ? pathname : '';
  if (
    /^\/(login|signup|confirm|recover-password|verify-email|onboarding)/.test(path) ||
    /^\/(order|sale)\//.test(path)
  ) {
    return false;
  }
  return true;
};
